import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CreateWorksheet() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [lessonPlans, setLessonPlans] = useState([]);
  const [settings, setSettings] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    grade: '',
    subject: '',
    topic: '',
    num_exercises: 5,
    lesson_plan_id: '',
    lesson_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/teacher/lesson-plans`),
        axios.get(`${API_URL}/api/teacher/settings`)
      ]);
      setLessonPlans(plansRes.data);
      setSettings(settingsRes.data);
      
      if (settingsRes.data) {
        setFormData(prev => ({
          ...prev,
          grade: settingsRes.data.grades?.[0] || '',
          subject: settingsRes.data.subjects?.[0] || ''
        }));
      }
    } catch (error) {
      console.error('Failed to fetch data', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Please enter a worksheet title');
      return;
    }
    if (!formData.topic.trim()) {
      toast.error('Please enter a topic');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/assessments/worksheets/generate`, formData);
      toast.success('Worksheet generated successfully!');
      navigate(`/assessments/worksheet/${response.data.worksheet_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate worksheet');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-sky-50" data-testid="create-worksheet-page">
      <header className="glass-header py-4 px-6">
        <div className="max-w-4xl mx-auto">
          <Button onClick={() => navigate('/assessments')} variant="ghost" data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Assessments
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="glass-card p-8">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-slate-900">Create Worksheet</h2>
              <p className="text-slate-600">Generate practice exercises for your students</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Worksheet Title *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Practice Exercises - Week 3"
                  className="w-full"
                  data-testid="worksheet-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Topic *</label>
                <Input
                  value={formData.topic}
                  onChange={(e) => setFormData(prev => ({ ...prev, topic: e.target.value }))}
                  placeholder="e.g., Addition and Subtraction"
                  className="w-full"
                  data-testid="worksheet-topic-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Grade</label>
                <select
                  value={formData.grade}
                  onChange={(e) => setFormData(prev => ({ ...prev, grade: e.target.value }))}
                  className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-white/80 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  data-testid="worksheet-grade-select"
                >
                  <option value="">Select Grade</option>
                  {settings?.grades?.map(g => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                  {!settings?.grades?.length && (
                    <>
                      <option value="Grade 1">Grade 1</option>
                      <option value="Grade 2">Grade 2</option>
                      <option value="Grade 3">Grade 3</option>
                      <option value="Grade 4">Grade 4</option>
                      <option value="Grade 5">Grade 5</option>
                      <option value="Grade 6">Grade 6</option>
                      <option value="Grade 7">Grade 7</option>
                    </>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Subject</label>
                <select
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-white/80 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  data-testid="worksheet-subject-select"
                >
                  <option value="">Select Subject</option>
                  {settings?.subjects?.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                  {!settings?.subjects?.length && (
                    <>
                      <option value="Mathematics">Mathematics</option>
                      <option value="English">English</option>
                      <option value="Life Skills">Life Skills</option>
                      <option value="Natural Sciences">Natural Sciences</option>
                    </>
                  )}
                </select>
              </div>
            </div>

            {/* Number of Exercises */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Number of Exercises</label>
              <div className="flex gap-2">
                {[3, 5, 7, 10].map(num => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, num_exercises: num }))}
                    className={`px-4 py-2 rounded-full font-medium transition-all ${
                      formData.num_exercises === num
                        ? 'bg-purple-600 text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                    data-testid={`exercises-${num}`}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>

            {/* Link to Lesson Plan (Optional) */}
            {lessonPlans.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Link to Lesson Plan (Optional)</label>
                <select
                  value={formData.lesson_plan_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, lesson_plan_id: e.target.value, lesson_id: '' }))}
                  className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-white/80 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  data-testid="lesson-plan-select"
                >
                  <option value="">No link - standalone worksheet</option>
                  {lessonPlans.map(plan => (
                    <option key={plan.plan_id} value={plan.plan_id}>
                      {plan.grade} - {plan.subject}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Info Box */}
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
              <h4 className="font-medium text-purple-900 mb-2">What's included in worksheets:</h4>
              <ul className="text-sm text-purple-700 space-y-1">
                <li>• Fill-in-the-blank exercises</li>
                <li>• Matching exercises</li>
                <li>• Problem-solving tasks</li>
                <li>• Application exercises</li>
                <li>• Answer key provided</li>
              </ul>
            </div>

            {/* Submit Button */}
            <div className="pt-6">
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-full py-6 text-lg font-semibold"
                data-testid="generate-worksheet-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating Worksheet...
                  </>
                ) : (
                  <>
                    <FileText className="w-5 h-5 mr-2" />
                    Generate Worksheet
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
